// Pure merge helpers for localStorage ∪ DB capability grants (SC-2 / RP-2c).

import type { CapabilityOverrides } from "./capabilityOverrides.js";

/**
 * Merge order for a single capability:
 *   1. explicit localStorage override (self-toggle)
 *   2. DB-backed admin grant/revoke
 *   3. role-derived default (caller supplies via `roleDefault`)
 *
 * Pure — no I/O. Used by `isCapabilityEffective` and unit tests for
 * cross-device merge behavior.
 */
export function mergeCapabilitySources(args: {
  localOverride: boolean | undefined;
  dbGrant: boolean | undefined;
  roleDefault: boolean;
}): boolean {
  if (args.localOverride !== undefined) return args.localOverride;
  if (args.dbGrant !== undefined) return args.dbGrant;
  return args.roleDefault;
}

/** Project a list of `{ capability, value }` rows into CapabilityOverrides. */
export function memberCapabilityRowsToOverrides(
  rows: Array<{ capability?: unknown; value?: unknown }>,
): CapabilityOverrides {
  const out: CapabilityOverrides = {};
  for (const row of rows) {
    const cap = typeof row.capability === "string" ? row.capability : "";
    if (cap === "is_ads_runner" && typeof row.value === "boolean") {
      out.is_ads_runner = row.value;
    }
  }
  return out;
}

/**
 * Admin Members checkbox display value: DB grant if present, else
 * role-derived default from the member's org roles (sales ⇒ true).
 * Does not consult the member's localStorage (admin can't see it).
 */
export function adminAdsRunnerCheckboxValue(args: {
  dbGrant: boolean | undefined;
  memberRoles: readonly string[];
}): boolean {
  if (args.dbGrant !== undefined) return args.dbGrant;
  const roles = new Set(args.memberRoles.map((r) => r.trim().toLowerCase()));
  return roles.has("sales");
}
